import { clsx } from "clsx";
import { type ScaleLinear } from "d3";
import { type FC } from "react";
import { useHover } from "react-aria";

import { TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";

import styleTrack from "../styles/Track.module.scss";

import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import { type Track, type TrackBedOrderedBedFile } from "../../../graphql.js";
import { type SetTooltipContent, ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { BedTrackLoader } from "./BedTrackLoader.js";

interface IBedTrackProps {
  track: Track;
  scale: ScaleLinear<number, number>;
  handleCustomize: () => void;
  setTooltipContent: SetTooltipContent;
}

const BedsTrack: FC<IBedTrackProps> = ({ track, ...props }) => {
  const { hoverProps, isHovered } = useHover({});
  const trackMessage = "Select Experiment Data (BED files) to display here";
  const emptyTrack = !track.trackCustomization?.bedConfiguration?.orderedBedFiles?.length;

  if (emptyTrack) {
    return (
      <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
        <TrackHeader
          isTrackHovered={isHovered}
          title={conditionallyPluralizeTrackLabel(track)}
          trackIcons={
            <TriggerButtonGroup vertical>
              <TriggerButton
                icon={"folder_open"}
                label={trackMessage}
                onPress={() => props.handleCustomize()}
                size={TriggerButtonSize.Small}
              />
            </TriggerButtonGroup>
          }
        />
        <div className={styleTrack.container}>
          <ShowMessageOverTrack message={trackMessage} onClick={props.handleCustomize} />
        </div>
      </div>
    );
  }

  return (
    <>
      {track.trackCustomization.bedConfiguration.orderedBedFiles.map((bedFile: TrackBedOrderedBedFile, index) => {
        return (
          <BedTrackLoader
            track={track}
            scale={props.scale}
            bedFileData={bedFile}
            key={`bed-track-${bedFile.bedFileId}-${index}`}
            setTooltipContent={props.setTooltipContent}
          />
        );
      })}
    </>
  );
};

export { BedsTrack };
